/**
 * @module Test/Publishing
 * @preferred
 *
 * Defines the methods required for the Publishing Test Flow
 */

/**
 * Publishing Test Flow
 */

/* tslint:enable */


import * as e from './errors/';

import subscriberMOS from './helpers/subscriberMOS';
import MOSState from './helpers/MOSState';
import config from './helpers/config';
import { AverageStats, Bandwidth, HasAudioVideo } from './types/stats';
import { NetworkTestOptions } from 'opentok-network-test-js';
import { UpdateQualityResultsCallback } from './types/callbacks';
import { pick } from './utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

interface QualityTestResultsBuilder {
  state: MOSState;
  subscriber: OT.Subscriber;
  mosScore?: number;
  bandwidth?: Bandwidth;
}

export interface QualityTestResults extends HasAudioVideo<AverageStats> {
  phase?: string;
  onUpdateParams?: any;
  showLostSignalModalBS?: BehaviorSubject<boolean>;
}

type MOSResultsCallback = (state: MOSState, that:any) => void;
type DeviceMap = { [deviceId: string]: OT.Device };

let audioOnly = false; // By default, the initial test is audio-video
let stopTest: Function | undefined;
let stopTestTimeoutCompleted = false;
let stopTestCalled = false;

function buildResults(builder: QualityTestResultsBuilder): QualityTestResults {
  const baseProps: (keyof AverageStats)[] = ['bitrate', 'packetLossRatio', 'supported', 'reason', 'mos'];
  builder.state.stats.audio.mos = builder.state.audioQualityScore();
  builder.state.stats.video.mos = builder.state.videoQualityScore();
  if (builder.state.stats.audio.mos >= config.qualityThresholds.audio[0].minMos) {
    builder.state.stats.audio.supported = true;
  } else {
    builder.state.stats.audio.supported = false;
    builder.state.stats.audio.reason = config.strings.bandwidthLow;
  }
  return {
    audio: pick(baseProps, builder.state.stats.audio),
    video: pick(baseProps.concat(['frameRate', 'recommendedResolution', 'recommendedFrameRate']),
      builder.state.stats.video),
  };
}

function isAudioQualityAcceptable(results: QualityTestResults): boolean {
  return !!results.audio.mos && (results.audio.mos >= config.qualityThresholds.audio[0].minMos);
}

/**
 * Clean subscriber objects before disconnecting from the session
 * @param session
 * @param subscriber
 */
function cleanSubscriber(session: OT.Session, subscriber: OT.Subscriber): Promise<void> {
  return new Promise((resolve:any) => {
    subscriber.on('destroyed', () => {
      resolve();
    });
    if (!subscriber) {
      resolve();
    }
  });
}

function checkSubscriberQuality(
  session: OT.Session,
  publisher: OT.Publisher,
  subscriber: OT.Subscriber,
  forceTestStop$: Observable<boolean>,
  options?: NetworkTestOptions,
  onUpdate?: UpdateQualityResultsCallback<QualityTestResults>,
  onUpdateParams?: any,
  audioOnlyFallback?: boolean,
  that?: any
): Promise<QualityTestResults> {

  return new Promise((resolve:any, reject:any) => {
    try {
      const builder: QualityTestResultsBuilder = {
        state: new MOSState(audioOnlyFallback),
        ...{ subscriber },
      };

      forceTestStop$.pipe(
        tap((value) => {
          processResults(that);
        })
      );

      const getStatsListener = (results: MOSState, that:any) => {
        const updateStats = (
          results: MOSState
        ): QualityTestResults => ({
          ...buildResults(builder),
          phase: audioOnly ? 'audio-only' : 'audio-video'
        });
        onUpdate &&
          onUpdate(updateStats(results), onUpdateParams, session, that);
      };

      const processResults = (that:any) => {
        const audioVideoResults: QualityTestResults = buildResults(builder);
        if (
          !audioOnly &&
          !isAudioQualityAcceptable(audioVideoResults) &&
          !stopTestCalled
        ) {
          audioOnly = true;
          // We don't want to lose the videoResults.
          const videoResults = audioVideoResults.video;
          checkSubscriberQuality(
            session,
            publisher,
            subscriber,
            forceTestStop$,
            undefined,
            onUpdate,
            null,
            undefined,
            that
          ).then((results: QualityTestResults) => {
            results.video = videoResults;
            cleanSubscriber(session, subscriber);
            resolve(results);
          });
        } else {
          session.on('sessionDisconnected', () => {
            resolve(audioVideoResults);
          });
          cleanSubscriber(session, subscriber);
        }
      };

      const resultsCallback: MOSResultsCallback = (state: MOSState, that:any) => {
        // clearTimeout(mosEstimatorTimeoutId);
        processResults(that);
      };

      subscriberMOS(
        builder.state,
        subscriber,
        publisher,
        getStatsListener,
        resultsCallback,
        forceTestStop$,
        onUpdateParams.bandwidthTestInterval,
        that
      );
    } catch (exception) {
      reject(new e.SubscriberGetStatsError());
    }
  });
}

/**
 * This method checks to see if the client can publish to an OpenTok session.
 */
export function videocallTestQuality(
  session: OT.Session,
  publisher: OT.Publisher,
  subscriber: OT.Subscriber,
  forceTestStop$: Observable<boolean>,
  options?: NetworkTestOptions,
  onUpdate?: UpdateQualityResultsCallback<QualityTestResults>,
  onUpdateParams?: any,
  that?: any
): Promise<QualityTestResults> {
  stopTestTimeoutCompleted = false;
  stopTestCalled = false;
  return new Promise((resolve, reject) => {
    const onSuccess = (results: QualityTestResults) => {
      stopTest = undefined;
      console.log({ action: 'testQuality', variation: 'Success' });
      resolve(results);
    };

    const onError = (error: Error) => {
      stopTest = undefined;
      console.log({ action: 'testQuality', variation: 'Failure' });
      reject(error);
    };

    checkSubscriberQuality(
      session,
      publisher,
      subscriber,
      forceTestStop$,
      undefined,
      onUpdate,
      onUpdateParams,
      undefined,
      that
    )
      .then(onSuccess)
      .catch(onError);
  });
}

export function stopQualityTest() {
  stopTestCalled = true;
  if (stopTestTimeoutCompleted && stopTest) {
    stopTest();
  }
}
